import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AGUSelectOption } from 'agu_app_shell';
import { ActiveFilterPill } from '../../finder/active-filter-pills';
import { WithAll } from '../../finder/select-options';
import { ResearchPeopleFacets, ResearchPeopleFilterInput, UnderstoodPeopleConstraint } from '../../models/research.models';

export const PEOPLE_QUERY_GROUP_FIELDS: Readonly<Record<string, ReadonlyArray<keyof ResearchPeopleFilterInput>>> = {
    organization: ['organization'],
    location: ['location'],
    researchArea: ['researchArea'],
    hasMutualCoauthors: ['hasMutualCoauthors'],
    recommendedOnly: ['recommendedOnly'],
    country: ['country'],
    state: ['state'],
    city: ['city'],
    degreeLevelMin: ['degreeLevelMin'],
    yearsExperience: ['yearsExperienceMin', 'yearsExperienceMax'],
    yearsExperienceMin: ['yearsExperienceMin'],
    yearsExperienceMax: ['yearsExperienceMax'],
    hasFunding: ['hasFunding'],
    hasPIFunding: ['hasPIFunding'],
    hasActiveFunding: ['hasActiveFunding'],
    mapsMember: ['mapsMember'],
};

export function PeoplePillGroupOf(kind: string): string {
    return kind === 'yearsExperienceMin' || kind === 'yearsExperienceMax' ? 'yearsExperience' : kind;
}

const DEGREE_LABELS: Readonly<Record<number, string>> = {
    1: "Bachelor's+",
    2: "Master's+",
    3: 'Doctorate',
};

const PEOPLE_PILL_LABEL: Readonly<Record<string, string>> = {
    organization: 'Organization',
    location: 'Location',
    researchArea: 'Research area',
    hasMutualCoauthors: 'Show',
    recommendedOnly: 'Show',
    country: 'Country',
    state: 'State',
    city: 'City',
    degreeLevelMin: 'Degree',
    yearsExperience: 'Experience',
    hasFunding: 'Funding',
    hasPIFunding: 'Funding',
    hasActiveFunding: 'Funding',
    mapsMember: 'Membership',
    careerStage: 'Career stage',
};

const PREFERRED_SUFFIX = ' · preferred';
const NOT_YET_APPLIED_SUFFIX = ' · not yet applied';

function FormatConstraintValue(group: string, values: readonly string[], minExp?: string, maxExp?: string): string {
    if (group === 'hasMutualCoauthors') return 'Mutual co-authors';
    if (group === 'recommendedOnly') return 'Suggested only';
    if (group === 'hasFunding') return 'Has grant funding';
    if (group === 'hasPIFunding') return 'PI on grant';
    if (group === 'hasActiveFunding') return 'Active grant';
    if (group === 'mapsMember') return 'MAPS member';
    if (group === 'degreeLevelMin') {
        const n = Number(values[0]);
        return DEGREE_LABELS[n] ?? values.join(', ');
    }
    if (group === 'yearsExperience') {
        if (minExp !== undefined && maxExp !== undefined) return `${minExp}–${maxExp} yrs`;
        if (minExp !== undefined) return `${minExp}+ yrs`;
        if (maxExp !== undefined) return `Up to ${maxExp} yrs`;
    }
    return values.join(', ');
}

/**
 * UI-question-level active-filter count for the toolbar's `ActiveFilterCount`
 * badge — mirrors `CountActiveGrantFilterGroups` (grant-filters.component.ts):
 * one bump per answered filter QUESTION sent to the server, never per the
 * client-only `ShowSavedOnly` toggle.
 */
export function CountActivePeopleFilterGroups(f: ResearchPeopleFilterInput): number {
    let count = 0;
    if (f.organization && f.organization.length > 0) count++;
    if (f.location && f.location.length > 0) count++;
    if (f.researchArea && f.researchArea.length > 0) count++;
    if (f.hasMutualCoauthors) count++;
    if (f.recommendedOnly) count++;
    if (f.country && f.country.length > 0) count++;
    if (f.state && f.state.length > 0) count++;
    if (f.city && f.city.length > 0) count++;
    if (f.degreeLevelMin !== undefined && f.degreeLevelMin !== null) count++;
    if (f.yearsExperienceMin !== undefined && f.yearsExperienceMin !== null) count++;
    else if (f.yearsExperienceMax !== undefined && f.yearsExperienceMax !== null) count++;
    if (f.hasFunding) count++;
    if (f.hasPIFunding) count++;
    if (f.hasActiveFunding) count++;
    if (f.mapsMember) count++;
    return count;
}

export function BuildPeopleFilterPills(
    f: ResearchPeopleFilterInput,
    constraints?: readonly UnderstoodPeopleConstraint[]
): ActiveFilterPill[] {
    if (Array.isArray(constraints)) {
        const pills: ActiveFilterPill[] = [];
        const minExp = constraints.find((c) => c.kind === 'yearsExperienceMin')?.values[0];
        const maxExp = constraints.find((c) => c.kind === 'yearsExperienceMax')?.values[0];
        for (const c of constraints) {
            const group = PeoplePillGroupOf(c.kind);
            const label = PEOPLE_PILL_LABEL[group];
            if (label === undefined) continue;
            if (pills.some((p) => p.Key === group)) continue;
            const baseValue = FormatConstraintValue(group, c.values, minExp, maxExp);
            const suffix =
                c.applier === 'unverifiable'
                    ? NOT_YET_APPLIED_SUFFIX
                    : c.stance === 'preference'
                      ? PREFERRED_SUFFIX
                      : '';
            pills.push({ Key: group, Label: label, Value: baseValue + suffix, ...(c.origin === 'query' ? { FromQuery: true } : {}) });
        }
        return pills;
    }

    const pills: ActiveFilterPill[] = [];
    if (f.organization && f.organization.length > 0) pills.push({ Key: 'organization', Label: 'Organization', Value: f.organization.join(', ') });
    if (f.location && f.location.length > 0) pills.push({ Key: 'location', Label: 'Location', Value: f.location.join(', ') });
    if (f.researchArea && f.researchArea.length > 0) pills.push({ Key: 'researchArea', Label: 'Research area', Value: f.researchArea.join(', ') });
    if (f.hasMutualCoauthors) pills.push({ Key: 'hasMutualCoauthors', Label: 'Show', Value: 'Mutual co-authors' });
    if (f.recommendedOnly) pills.push({ Key: 'recommendedOnly', Label: 'Show', Value: 'Suggested only' });
    if (f.country && f.country.length > 0) pills.push({ Key: 'country', Label: 'Country', Value: f.country.join(', ') });
    if (f.state && f.state.length > 0) pills.push({ Key: 'state', Label: 'State', Value: f.state.join(', ') });
    if (f.city && f.city.length > 0) pills.push({ Key: 'city', Label: 'City', Value: f.city.join(', ') });
    if (f.degreeLevelMin !== undefined && f.degreeLevelMin !== null) {
        pills.push({ Key: 'degreeLevelMin', Label: 'Degree', Value: DEGREE_LABELS[f.degreeLevelMin] ?? String(f.degreeLevelMin) });
    }
    if (f.yearsExperienceMin !== undefined && f.yearsExperienceMin !== null || f.yearsExperienceMax !== undefined && f.yearsExperienceMax !== null) {
        const minStr = f.yearsExperienceMin !== undefined && f.yearsExperienceMin !== null ? String(f.yearsExperienceMin) : undefined;
        const maxStr = f.yearsExperienceMax !== undefined && f.yearsExperienceMax !== null ? String(f.yearsExperienceMax) : undefined;
        pills.push({ Key: 'yearsExperience', Label: 'Experience', Value: FormatConstraintValue('yearsExperience', [], minStr, maxStr) });
    }
    if (f.hasFunding) pills.push({ Key: 'hasFunding', Label: 'Funding', Value: 'Has grant funding' });
    if (f.hasPIFunding) pills.push({ Key: 'hasPIFunding', Label: 'Funding', Value: 'PI on grant' });
    if (f.hasActiveFunding) pills.push({ Key: 'hasActiveFunding', Label: 'Funding', Value: 'Active grant' });
    if (f.mapsMember) pills.push({ Key: 'mapsMember', Label: 'Membership', Value: 'MAPS member' });
    return pills;
}

export function RemovePeopleFilterGroup(f: ResearchPeopleFilterInput, key: string): ResearchPeopleFilterInput {
    switch (key) {
        case 'organization': return { ...f, organization: [] };
        case 'location': return { ...f, location: [] };
        case 'researchArea': return { ...f, researchArea: [] };
        case 'hasMutualCoauthors': return { ...f, hasMutualCoauthors: false };
        case 'recommendedOnly': return { ...f, recommendedOnly: false };
        case 'country': return { ...f, country: [] };
        case 'state': return { ...f, state: [] };
        case 'city': return { ...f, city: [] };
        case 'degreeLevelMin': return { ...f, degreeLevelMin: undefined };
        case 'yearsExperience':
        case 'yearsExperienceMin':
        case 'yearsExperienceMax':
            return { ...f, yearsExperienceMin: undefined, yearsExperienceMax: undefined };
        case 'hasFunding': return { ...f, hasFunding: false };
        case 'hasPIFunding': return { ...f, hasPIFunding: false };
        case 'hasActiveFunding': return { ...f, hasActiveFunding: false };
        case 'mapsMember': return { ...f, mapsMember: false };
        default: return f;
    }
}

/**
 * Collaborator Finder's filter panel — projected into `ra-finder-toolbar`'s
 * `[filters]` slot. Two-way binds `Filters` (`ResearchPeopleFilterInput`, the
 * wire shape sent to `researchPeopleSearch`) plus `ShowSavedOnly` (client-only
 * filter over the already-fetched list) and `RecommendedOnly` (server-side
 * `filters.recommendedOnly` filter on the All collaborators tab, mockup label
 * "Suggested only").
 *
 * `organization`, `location` and `researchArea` are the wire's multi-value
 * lists (spec D6: applied AFTER Pinecone recall, so there is no server-side
 * reason to cap any at one value) but the RAIL itself is three plain native
 * `<select>`s per the mockup (`collaborators.html:658-660`, `fillSel()`
 * `:807-809`) — R-F18/P-C9: the mockup never renders a multi-select here, so
 * a member can only narrow each group to one value at a time from this rail.
 * Each select carries the array's first (only) value and writes back a
 * one-element array, or an empty one for "All ___".
 */
@Component({
    standalone: false,
    selector: 'ra-people-filters',
    templateUrl: './people-filters.component.html',
})
export class PeopleFiltersComponent {
    @Input() Filters: ResearchPeopleFilterInput = {};
    @Output() FiltersChange = new EventEmitter<ResearchPeopleFilterInput>();
    @Input() Facets: ResearchPeopleFacets | null = null;

    /** Client-only: filters the already-fetched list to `itemState.saved` items. Not sent to the server. */
    @Input() ShowSavedOnly = false;
    @Output() ShowSavedOnlyChange = new EventEmitter<boolean>();
    /** Mirrors `state.filters.recommendedOnly === true` — server-side filter on the All collaborators tab (mockup label: "Suggested only"). */
    @Input() RecommendedOnly = false;
    @Output() RecommendedOnlyChange = new EventEmitter<boolean>();

    get OrganizationSelect(): AGUSelectOption[] { return WithAll('All organizations', this.Facets?.organizations ?? []); }
    get LocationSelect(): AGUSelectOption[] { return WithAll('All locations', this.Facets?.locations ?? []); }
    get ResearchAreaSelect(): AGUSelectOption[] { return WithAll('All research areas', this.Facets?.researchAreas ?? []); }

    get OrganizationValue(): string { return this.Filters.organization?.[0] ?? ''; }
    OnOrganizationChange(value: string): void { this.Emit({ ...this.Filters, organization: value ? [value] : [] }); }

    get LocationValue(): string { return this.Filters.location?.[0] ?? ''; }
    OnLocationChange(value: string): void { this.Emit({ ...this.Filters, location: value ? [value] : [] }); }

    get ResearchAreaValue(): string { return this.Filters.researchArea?.[0] ?? ''; }
    OnResearchAreaChange(value: string): void { this.Emit({ ...this.Filters, researchArea: value ? [value] : [] }); }

    get HasMutualCoauthors(): boolean {
        return this.Filters.hasMutualCoauthors === true;
    }

    OnHasMutualCoauthorsChange(value: boolean): void {
        this.Emit({ ...this.Filters, hasMutualCoauthors: value });
    }

    private Emit(next: ResearchPeopleFilterInput): void {
        this.FiltersChange.emit(next);
    }
}
